import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faUpload,
    faSpinner,
    faFile,
    faCircleCheck,
    faCircleXmark,
    faFolder,
    faClipboardCheck,
    faEye,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { httpClient } from "../HttpClient";
import "../assets/css/IrModels.css";
import "../assets/css/OtaArtifacts.css";

const IR_API = process.env.REACT_APP_IR_MODELS_URL || 'https://dev.mangoe.com/api/device_mgmt_service/admin/ir-json';

function computeS3Path(category, brand, modelId) {
    if (!category || !brand || !modelId) return "";
    return `ir/${category}/${brand}/${modelId}.json`;
}

function UploadIrJson() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Support both URL search params (survives refresh) and navigation state (legacy)
    const prefill = {
        category: searchParams.get("category") || location.state?.category || "",
        brand: searchParams.get("brand") || location.state?.brand || "",
        modelId: searchParams.get("modelId") || location.state?.modelId || "",
    };
    const isUpdate = !!prefill.modelId;

    // Dropdown data from API
    const [allModels, setAllModels] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);

    // Category
    const [category, setCategory] = useState(prefill.category || "");

    // Brand
    const [brand, setBrand] = useState(prefill.brand || "");

    // Model ID
    const [modelId, setModelId] = useState(prefill.modelId || "");

    // File
    const [file, setFile] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Validation
    const [validationStatus, setValidationStatus] = useState(null);
    const [validationMessage, setValidationMessage] = useState("");

    // Upload state
    const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'success'

    // Current deployed JSON preview (Update only)
    const [currentJson, setCurrentJson] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const effectiveCategory = category.trim();
    const effectiveBrand = brand.trim();
    const effectiveModelId = modelId.trim();
    const s3Path = computeS3Path(effectiveCategory, effectiveBrand, effectiveModelId);

    const categoryOptions = [...new Set(allModels.map(m => m.category))].sort();
    const brandOptions = effectiveCategory
        ? [...new Set(allModels.filter(m => m.category?.toLowerCase() === effectiveCategory.toLowerCase()).map(m => m.brand))].sort()
        : [];
    // Fetch all models for dropdowns
    useEffect(() => {
        setLoadingDropdowns(true);
        httpClient.get(`${IR_API}/models`, { params: { size: 500 } })
            .then(res => {
                if (res.data?.success) setAllModels(res.data.data.content || []);
            })
            .catch(() => {})
            .finally(() => setLoadingDropdowns(false));
    }, []);

    // Fetch current deployed JSON for preview (Update page only)
    useEffect(() => {
        if (!isUpdate || !prefill.category || !prefill.brand || !prefill.modelId) return;
        setLoadingPreview(true);
        const s3Key = computeS3Path(prefill.category, prefill.brand, prefill.modelId);
        httpClient.get(`${IR_API}/file`, { params: { s3Key } })
            .then(res => {
                if (res.data?.success) {
                    const data = res.data.data;
                    setCurrentJson(typeof data === "string" ? JSON.parse(data) : data);
                }
            })
            .catch(() => setCurrentJson(null))
            .finally(() => setLoadingPreview(false));
    }, []); // eslint-disable-line

    const resetValidation = () => {
        setValidationStatus(null);
        setValidationMessage("");
    };

    const handleCategoryChange = (val) => {
        setCategory(val);
        setBrand("");
        setModelId("");
        resetValidation();
    };

    const handleBrandChange = (val) => {
        setBrand(val);
        setModelId("");
        resetValidation();
    };

    const processFile = (f) => {
        if (!f) return;
        if (!f.name.endsWith(".json")) {
            toast.error("Only .json files are accepted.");
            return;
        }
        const fileBaseName = f.name.replace(/\.json$/i, "");
        if (effectiveModelId && fileBaseName !== effectiveModelId) {
            toast.error(`File name must match the Model ID. Expected "${effectiveModelId}.json" but got "${f.name}".`);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const parsed = JSON.parse(e.target.result);
                setFile(f);
                setFileContent(parsed);
                resetValidation();
            } catch {
                toast.error("File is not valid JSON. Please check the file and try again.");
            }
        };
        reader.readAsText(f);
    };

    const handleFileChange = (e) => processFile(e.target.files?.[0]);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        processFile(e.dataTransfer.files?.[0]);
    };

    const handleValidate = async () => {
        if (!s3Path) { toast.warn("Please select Category, Brand, and Model ID first."); return; }
        if (!file) { toast.warn("Please select a .json file to upload."); return; }
        setValidationStatus("validating");
        const formData = new FormData();
        formData.append("file", file);
        try {
            const res = await httpClient.post(`${IR_API}/validate`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.success && res.data.data?.valid) {
                setValidationStatus("success");
                setValidationMessage("JSON is valid and ready to upload.");
            } else {
                const errors = res.data?.data?.errors || [];
                setValidationStatus("error");
                setValidationMessage(errors.join(" · ") || "Validation failed.");
            }
        } catch (err) {
            setValidationStatus("error");
            setValidationMessage(err.response?.data?.error?.message || "Validation request failed.");
        }
    };

    const handleConfirm = async () => {
        if (!canUpload || uploadStatus === "uploading") return;
        setUploadStatus("uploading");
        const formData = new FormData();
        formData.append("category", effectiveCategory);
        formData.append("brand", effectiveBrand);
        formData.append("modelId", effectiveModelId);
        formData.append("file", file);
        try {
            const res = await httpClient.post(`${IR_API}/upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.success) {
                if (res.data.data?.newModel) {
                    toast.info("New model created. Note: not visible in mobile app until a mapping row is added.");
                }
                toast.success(`IR JSON for ${effectiveModelId} uploaded successfully.`);
                navigate("/ir-models", { state: { highlightedS3Key: res.data.data?.s3Key } });
            }
        } catch (err) {
            setUploadStatus(null);
            const error = err.response?.data?.error;
            const msg = error?.message || "Upload failed. Please try again.";
            const details = Array.isArray(error?.details) ? error.details.join(", ") : null;
            toast.error(details ? `${msg}: ${details}` : msg);
        }
    };

    const canValidate = !!(effectiveCategory && effectiveBrand && effectiveModelId && file);
    const canUpload = validationStatus === "success";

    return (
        <div className="custom_form_wrapper ir-upload-page">
            {/* Header — matches Update User Details pattern */}
            <div className="custom_form_header">
                <div>
                    <p className="form_head_title mb-0" style={{ textTransform: "none" }}>
                        {isUpdate ? `Update: ${prefill.modelId}` : "Upload IR JSON"}
                    </p>
                    <p className="ir-page-subtitle">Deploy a new or corrected IR JSON file to S3</p>
                </div>
                <div className="action_cta">
                    <button className="btn page_back_btn" onClick={() => navigate("/ir-models")}>
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 6 }} />Back
                    </button>
                </div>
            </div>

            {/* Two-panel layout */}
            <div className={`custom_form_body_wrapper${isUpdate ? " ir-compare-body" : ""}`}>
            <div className="ir-upload-layout">

                {/* ── LEFT: upload form ── */}
                <div className={`ir-upload-form-panel${isUpdate ? " ir-update-form-panel" : ""}`}>
                    <div className="ir-form-card">
                        <h6 className="ir-card-title">{isUpdate ? "Replace File" : "Upload New File"}</h6>

                        {/* On Update show locked identity badge; on Add show cascading dropdowns */}
                        {isUpdate ? (
                            <div className="ir-update-device-badge">
                                <span className="ir-update-badge-chip">{prefill.category}</span>
                                <span className="ir-update-badge-arrow">→</span>
                                <span className="ir-update-badge-chip">{prefill.brand}</span>
                                <span className="ir-update-badge-arrow">→</span>
                                <span className="ir-update-badge-chip ir-update-badge-model">{prefill.modelId}</span>
                            </div>
                        ) : (
                            <>
                                <p className="ir-select-device-label">Select Device</p>
                                <div className="ir-device-dropdowns-row">
                                    {/* Category */}
                                    <div className="ir-dropdown-col">
                                        <label className="ir-dropdown-label">CATEGORY</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={category}
                                            onChange={e => handleCategoryChange(e.target.value)}
                                            disabled={loadingDropdowns}
                                        >
                                            <option value="">Select...</option>
                                            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>

                                    <div className="ir-dropdown-arrow">→</div>

                                    {/* Brand */}
                                    <div className="ir-dropdown-col">
                                        <label className="ir-dropdown-label">BRAND</label>
                                        <select
                                            className="form-select form-select-sm"
                                            value={brand}
                                            onChange={e => handleBrandChange(e.target.value)}
                                            disabled={!effectiveCategory || loadingDropdowns}
                                        >
                                            <option value="">Select...</option>
                                            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                    </div>

                                    <div className="ir-dropdown-arrow">→</div>

                                    {/* Model ID */}
                                    <div className="ir-dropdown-col">
                                        <label className="ir-dropdown-label">MODEL ID</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm ir-new-entry-input"
                                            placeholder="e.g. 1160"
                                            value={modelId}
                                            onChange={e => { setModelId(e.target.value); resetValidation(); }}
                                            disabled={!effectiveBrand}
                                            style={{ color: "#212529", WebkitTextFillColor: "#212529" }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* S3 path pill — only on Add */}
                        {!isUpdate && s3Path && (
                            <div className="ir-s3-pill">
                                <FontAwesomeIcon icon={faFolder} className="ir-s3-pill-icon" />
                                <code>{s3Path}</code>
                            </div>
                        )}

                        {!isUpdate && <hr className="my-3" />}

                        {/* File Upload */}
                        <p className="ir-select-device-label">File Upload</p>

                        <div
                            className={`ir-dropzone${isDragOver ? " ir-dropzone--active" : ""}${file ? " ir-dropzone--has-file" : ""}`}
                            onDrop={handleDrop}
                            onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                            onDragLeave={() => setIsDragOver(false)}
                            onClick={() => fileInputRef.current?.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => e.key === "Enter" && fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                accept=".json,application/json"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                                onClick={e => { e.target.value = null; }}
                            />
                            {file ? (
                                <div className="ir-dropzone-file">
                                    <FontAwesomeIcon icon={faFile} className="ir-dropzone-file-icon" />
                                    <div className="ir-dropzone-file-info">
                                        <p className="ir-dropzone-filename">{file.name}</p>
                                        <p className="ir-dropzone-filesize">{(file.size / 1024).toFixed(1)} KB</p>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-outline-danger ms-auto"
                                        onClick={e => {
                                            e.stopPropagation();
                                            setFile(null);
                                            setFileContent(null);
                                            resetValidation();
                                            if (fileInputRef.current) fileInputRef.current.value = "";
                                        }}
                                    >Remove</button>
                                </div>
                            ) : (
                                <div className="ir-dropzone-placeholder">
                                    <FontAwesomeIcon icon={faUpload} className="ir-dropzone-icon" />
                                    <p className="ir-dropzone-text">Drag and drop your <strong>.json</strong> file here</p>
                                    <p className="ir-dropzone-subtext">or click to browse</p>
                                </div>
                            )}
                        </div>

                        {/* Validation feedback */}
                        {validationStatus && (
                            <div className={`ir-validation-status ir-validation-${validationStatus}`}>
                                {validationStatus === "validating" && (
                                    <><FontAwesomeIcon icon={faSpinner} className="spin-icon" /> Validating…</>
                                )}
                                {validationStatus === "success" && (
                                    <><FontAwesomeIcon icon={faCircleCheck} /> {validationMessage}</>
                                )}
                                {validationStatus === "error" && (
                                    <><FontAwesomeIcon icon={faCircleXmark} /> {validationMessage}</>
                                )}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div className="ir-upload-actions">
                            <button
                                className="btn ir-validate-btn"
                                onClick={handleValidate}
                                disabled={!canValidate || validationStatus === "validating"}
                            >
                                {validationStatus === "validating" ? (
                                    <><FontAwesomeIcon icon={faSpinner} className="spin-icon" style={{ marginRight: 6 }} />Validating…</>
                                ) : (
                                    <><FontAwesomeIcon icon={faClipboardCheck} style={{ marginRight: 6 }} />Validate JSON</>
                                )}
                            </button>
                            <button
                                className={`btn primary_btn btn-loader${uploadStatus === "uploading" ? " loading" : ""}`}
                                disabled={!canUpload || uploadStatus === "uploading"}
                                onClick={handleConfirm}
                                style={{ width: "100%" }}
                            >
                                {uploadStatus === "uploading" ? "Uploading…" : (
                                    <><FontAwesomeIcon icon={faEye} style={{ marginRight: 6 }} />Confirm &amp; Upload</>
                                )}
                            </button>
                            <button
                                className="btn ir-cancel-link"
                                onClick={() => navigate("/ir-models")}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── RIGHT: side-by-side on Update, single card on Add ── */}
                <div className="ir-upload-preview-panel">
                    {isUpdate ? (
                        <div className="ir-preview-compare">
                            {/* Current deployed */}
                            <div className="ir-form-card ir-preview-card-current ir-preview-compare-panel">
                                <p className="ir-preview-card-label ir-preview-label-current">
                                    Current Deployed — {prefill.modelId}.json
                                </p>
                                {loadingPreview ? (
                                    <div className="ir-preview-loading">
                                        <FontAwesomeIcon icon={faSpinner} className="spin-icon" />
                                        <span>Loading…</span>
                                    </div>
                                ) : currentJson ? (
                                    <div className="ir-file-json-preview">
                                        <pre>{JSON.stringify(currentJson, null, 2)}</pre>
                                    </div>
                                ) : (
                                    <p className="ir-preview-fetch-error">
                                        Could not load current deployment.
                                    </p>
                                )}
                            </div>

                            {/* New upload */}
                            <div className="ir-form-card ir-preview-card-new ir-preview-compare-panel">
                                <p className="ir-preview-card-label ir-preview-label-new">
                                    New Upload — {file?.name || "—"}
                                </p>
                                {fileContent ? (
                                    <div className="ir-file-json-preview">
                                        <pre>{JSON.stringify(fileContent, null, 2)}</pre>
                                    </div>
                                ) : (
                                    <div className="ir-deployed-empty">
                                        <FontAwesomeIcon icon={faFile} className="ir-deployed-empty-icon" />
                                        <p className="ir-deployed-empty-text">
                                            Drop the new .json file to compare with current.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="ir-preview-stack">
                            <div className="ir-form-card">
                                {fileContent ? (
                                    <>
                                        <p className="ir-preview-card-label ir-file-preview-title">
                                            File Preview — {file?.name}
                                        </p>
                                        <div className="ir-file-json-preview">
                                            <pre>{JSON.stringify(fileContent, null, 2)}</pre>
                                        </div>
                                    </>
                                ) : (
                                    <div className="ir-deployed-empty">
                                        <FontAwesomeIcon icon={faFile} className="ir-deployed-empty-icon" />
                                        <p className="ir-deployed-empty-text">
                                            Drop a .json file to preview it here.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            </div>

        </div>
    );
}

export default UploadIrJson;
